/*
================================================================================
FILE HEADER — apps/studio-web/src/components/RecorderPanel.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                 | What Changed           |
|----|------------|-------------------|---------------------|------------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Mimiq layout match  | Full recorder right    |
|    |            |                   |                     | panel: connected       |
|    |            |                   |                     | banner, drive/inspect, |
|    |            |                   |                     | actions, steps, replay,|
|    |            |                   |                     | test script            |
| 2  | 2026-06-25 | Claude Sonnet 4.6 | Drag-to-reorder     | Pass onReorderStep     |
|    |            |                   |                     | prop through to        |
|    |            |                   |                     | StepList               |
| 3  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist   | Added ✨ Assist as a   |
| 4  | 2026-11-05 | Claude Opus 5.5   | Public hosting + rename | Visible name Plex → Scriptwave; hosted at scriptwave.qagreen.com |
|    |            |                   | mode                | third segment; renders |
|    |            |                   |                     | AssistBar; hides Drive |
|    |            |                   |                     | instruction/recording  |
|    |            |                   |                     | UI while Assist active |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/App.tsx

EXPORTS COVERED
---------------
- RecorderPanel — right side of the Record tab, matching Mimiq's layout:
    Section 1: Connected banner
    Section 2: Drive / Inspect toggle
    Section 3: Action buttons (conditional on mode + recording state)
    Section 4: CUSTOM COMMANDS stub (tabbed)
    Section 5: Step list + instruction text
    Section 6: Replay section
    Section 7: Test Script collapsible

IMPORTANT POINTS
----------------
1. During recording: ONLY "● Stop Recording" button is shown (Mimiq behaviour).
2. During Inspect mode: no "Start Recording" button — inspect is read-only.
3. "+ New Test" = clear steps and reset to an unsaved state.
4. "Load" = triggers a file picker for importing a .json IR file.
5. "Download" = export current steps as IR JSON to downloads.
================================================================================
*/

import { useState } from 'react';
import type { BridgeStatus, ExtensionBridge } from '../extension/bridge.js';
import type { RecordMode, SerializedAXNode, SerializedAXTree } from '../extension/protocol.js';
import { newId } from '@plex/ir';
import type { IRStep, Candidate, Selector } from '@plex/ir';
import { buildTestIR } from '../ir/builder.js';
import { generatePlaywrightSpec } from '../ir/playwright-codegen.js';
import { generateHTMLReport } from '../ir/report-gen.js';
import { StepList } from './StepList.js';
import { CustomCommandsPanel } from './CustomCommandsPanel.js';
import { AssistBar } from './AssistBar.js';

export interface NetworkEvent {
  requestId: string;
  method: string;
  url: string;
  status: number;
  durationMs: number;
  requestBody?: string;
  mimeType?: string;
  capturedAt: number;
}

type Props = {
  bridge: ExtensionBridge;
  bridgeStatus: BridgeStatus;
  activeTabTitle: string;
  activeTabId: number;
  mode: RecordMode;
  isRecording: boolean;
  steps: IRStep[];
  replayingIndex: number | null;
  stepStatuses: Record<string, 'ok' | 'not_found' | 'error'>;
  networkEvents: NetworkEvent[];
  unsaved: boolean;
  loadedTestName: string;
  editingFunctionName?: string;    // set when editing a Function (not a test)
  onSaveFunction?: () => void;     // save steps back to the function being edited
  // Tier-2 Ollama
  tier2Enabled?: boolean;
  draftStepsResult?: { status: 'idle' | 'loading' | 'done' | 'error'; text: string };
  onRequestDraftSteps?: () => void;
  onSetMode: (m: RecordMode) => void;
  onToggleRecord: () => void;
  onNewTest: () => void;
  onClear: () => void;
  onSaveTest: () => void;
  onLoad: () => void;
  onDownload: () => void;
  onDeleteStep:        (index: number) => void;
  onEditStep:          (index: number, patch: Record<string, unknown>) => void;
  onEditCandidates:    (index: number, candidates: Candidate[]) => void;
  onReorderStep:       (from: number, to: number) => void;
  onAddSteps:          (steps: IRStep[]) => void;
  onReplay:            () => void;
  onAddNetworkAssert:  (event: NetworkEvent) => void;
  onClearNetworkEvents: () => void;
  onRequestCapture:    (callback: (sel: Selector | null) => void) => void;
  captureActive:       boolean;
  inspectedNode?:      SerializedAXNode | null;
  // AI Assist mode (Phase 6) — third segment alongside Drive/Inspect
  aiAssistEnabled?:    boolean;
  assistModeActive?:   boolean;
  onSetAssistMode?:    (active: boolean) => void;
  onRequestTree?:      () => Promise<SerializedAXTree | null>;
};

export function RecorderPanel({
  bridgeStatus, activeTabTitle, activeTabId,
  mode, isRecording, steps, replayingIndex, stepStatuses, networkEvents,
  unsaved, loadedTestName, editingFunctionName, onSaveFunction,
  tier2Enabled, draftStepsResult, onRequestDraftSteps,
  onSetMode, onToggleRecord, onNewTest, onClear, onSaveTest, onLoad, onDownload,
  onDeleteStep, onEditStep, onEditCandidates, onReorderStep, onAddSteps, onReplay,
  onAddNetworkAssert, onClearNetworkEvents, onRequestCapture, captureActive, inspectedNode,
  aiAssistEnabled, assistModeActive, onSetAssistMode, onRequestTree,
}: Props) {
  const [scriptOpen, setScriptOpen]   = useState(false);
  const [scriptMode, setScriptMode]   = useState<'json' | 'playwright'>('json');
  // When CommandBuilder is open, it takes the full panel height.
  // RecorderPanel hides the step list and gives all remaining space to the builder.
  const [cmdBuilderActive, setCmdBuilderActive] = useState(false);
  const connected = bridgeStatus === 'connected' && activeTabId >= 0;

  const ir         = steps.length > 0 ? buildTestIR(loadedTestName || 'Untitled', steps) : null;
  const scriptJson = ir ? JSON.stringify(ir, null, 2) : '';
  const scriptPw   = ir ? generatePlaywrightSpec(ir) : '';

  const passCount = Object.values(stepStatuses).filter(s => s === 'ok').length;
  const replayRan = Object.keys(stepStatuses).length > 0;

  const openReport = () => {
    const runResult = {
      testName: loadedTestName || 'Recorded test',
      startedAt: Date.now() - 5000,
      finishedAt: Date.now(),
      steps: steps.map(s => ({
        stepId:    s.id,
        stepType:  s.type,
        status:    (stepStatuses[s.id] ?? 'skipped') as 'ok' | 'not_found' | 'error' | 'skipped',
        matchedBy: undefined as string | undefined,
        durationMs: 0,
        detail:    (s as { label?: string }).label,
      })),
    };
    const html = generateHTMLReport([runResult]);
    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // The panel uses a fixed flex column so each section stays in place.
  // Only the step list + bottom sections scroll; the header (connected banner,
  // Drive/Inspect, action buttons, custom commands) always stays visible.
  return (
    <div style={{
      flex: '0 0 50%',
      borderLeft: '1px solid #27272a',
      display: 'flex', flexDirection: 'column',
      background: '#0f0f11',
      overflow: 'hidden',   // panel itself does NOT scroll
      minWidth: 340,
    }}>
      {/* ── Connected banner ── */}
      <div style={{
        padding: '10px 14px',
        background: connected ? 'rgba(34,197,94,0.08)' : 'rgba(113,113,122,0.08)',
        borderBottom: '1px solid #27272a',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
          background: connected ? '#22c55e' : '#52525b',
        }} />
        {connected ? (
          <span style={{ fontSize: 12, color: '#86efac', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Connected to <strong>{activeTabTitle}</strong>
          </span>
        ) : (
          <span style={{ fontSize: 12, color: '#71717a' }}>Not connected — click Record to attach a tab</span>
        )}
      </div>

      {/* ── Recorder step count ── */}
      <div style={{ padding: '8px 14px', borderBottom: '1px solid #1c1c1f', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#a1a1aa', flex: 1 }}>
          Recorder&nbsp;&nbsp;<span style={{ color: '#e4e4e7' }}>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
        </span>
        {unsaved && steps.length > 0 && (
          <span style={{ fontSize: 10, color: '#fbbf24', border: '1px solid #78350f', borderRadius: 20, padding: '2px 8px', background: '#1c1404' }}>
            ✏ Unsaved test
          </span>
        )}
        {loadedTestName && !unsaved && (
          <span style={{ fontSize: 10, color: '#86efac', border: '1px solid #166534', borderRadius: 20, padding: '2px 8px', background: '#052e16', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📄 {loadedTestName}
          </span>
        )}
      </div>

      {/* ── Drive / Inspect / Assist toggle ──
           Assist is a studio-side UI overlay, NOT a distinct extension mode —
           the underlying bridge `mode` stays whatever it was (Drive by default)
           so 🎯 Pick Element (CAPTURE_ELEMENT_AT) keeps working unmodified. */}
      <div style={{ display: 'flex', borderBottom: '1px solid #27272a' }}>
        <button
          onClick={() => { onSetAssistMode?.(false); onSetMode('drive'); }}
          style={{
            flex: 1, padding: '10px',
            background: !assistModeActive && mode === 'drive' ? 'rgba(34,197,94,0.1)' : 'none',
            border: 'none', borderBottom: !assistModeActive && mode === 'drive' ? '2px solid #22c55e' : '2px solid transparent',
            color: !assistModeActive && mode === 'drive' ? '#86efac' : '#71717a',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: !assistModeActive && mode === 'drive' ? '#22c55e' : '#52525b' }} />
          Drive
        </button>
        <button
          onClick={() => { onSetAssistMode?.(false); onSetMode('inspect'); }}
          style={{
            flex: 1, padding: '10px',
            background: !assistModeActive && mode === 'inspect' ? 'rgba(96,165,250,0.1)' : 'none',
            border: 'none', borderBottom: !assistModeActive && mode === 'inspect' ? '2px solid #60a5fa' : '2px solid transparent',
            color: !assistModeActive && mode === 'inspect' ? '#93c5fd' : '#71717a',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          🔍 Inspect
        </button>
        {aiAssistEnabled && (
          <button
            onClick={() => onSetAssistMode?.(true)}
            style={{
              flex: 1, padding: '10px',
              background: assistModeActive ? 'rgba(124,58,237,0.12)' : 'none',
              border: 'none', borderBottom: assistModeActive ? '2px solid #a78bfa' : '2px solid transparent',
              color: assistModeActive ? '#c4b5fd' : '#71717a',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}
          >
            ✨ Assist
          </button>
        )}
      </div>

      {/* ── Function editing banner ── */}
      {editingFunctionName && (
        <div style={{
          padding: '6px 14px', background: 'rgba(124,58,237,0.12)',
          borderBottom: '1px solid rgba(124,58,237,0.3)',
          display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
        }}>
          <span style={{ fontSize: 13, color: '#a78bfa' }}>⚙</span>
          <span style={{ fontSize: 12, color: '#c4b5fd', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Editing function: <strong>{editingFunctionName}</strong>
          </span>
          {onSaveFunction && (
            <button
              onClick={onSaveFunction}
              disabled={steps.length === 0}
              style={{
                background: '#7c3aed', border: 'none', borderRadius: 5,
                color: '#fff', fontSize: 11, fontWeight: 600,
                padding: '4px 10px', cursor: steps.length > 0 ? 'pointer' : 'not-allowed',
                opacity: steps.length > 0 ? 1 : 0.5,
              }}
            >
              ✓ Save Function
            </button>
          )}
        </div>
      )}

      {/* ── Action buttons ── */}
      <div style={{ padding: '8px 14px', borderBottom: '1px solid #1c1c1f', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {isRecording ? (
          /* Recording state: only Stop Recording */
          <button
            onClick={onToggleRecord}
            style={{ ...actionBtn, background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', flex: 1 }}
          >
            ● Stop Recording
          </button>
        ) : (
          /* Not recording */
          <>
            <button onClick={onNewTest} style={actionBtn}>+ New Test</button>
            {mode === 'drive' && !assistModeActive && (
              <button
                onClick={onToggleRecord}
                style={{ ...actionBtn, background: '#7c3aed', color: '#fff', border: 'none' }}
              >
                ▶ Start Recording
              </button>
            )}
            <button onClick={onClear} style={actionBtn} disabled={steps.length === 0}>Clear</button>
            {editingFunctionName ? (
              onSaveFunction && (
                <button onClick={onSaveFunction} style={{ ...actionBtn, background: 'rgba(124,58,237,0.2)', borderColor: '#7c3aed', color: '#c4b5fd' }} disabled={steps.length === 0}>
                  ✓ Save Function
                </button>
              )
            ) : (
              <button onClick={onSaveTest} style={actionBtn} disabled={steps.length === 0}>Save As</button>
            )}
            <button onClick={onLoad} style={actionBtn}>Load</button>
            <button onClick={onDownload} style={actionBtn} disabled={steps.length === 0}>↓ Download</button>
            {/* Tier-2: Suggest Steps button — only shown when AI is enabled and a tab is attached */}
            {tier2Enabled && activeTabId >= 0 && onRequestDraftSteps && (
              <button
                onClick={onRequestDraftSteps}
                disabled={draftStepsResult?.status === 'loading'}
                title="Ask local AI to suggest test steps from the current page"
                style={{
                  ...actionBtn,
                  background: 'rgba(124,58,237,0.15)',
                  border: '1px solid rgba(124,58,237,0.4)',
                  color: '#a78bfa',
                  opacity: draftStepsResult?.status === 'loading' ? 0.6 : 1,
                }}
              >
                {draftStepsResult?.status === 'loading' ? '🤖 ⟳' : '🤖 Suggest Steps'}
              </button>
            )}
          </>
        )}
      </div>

      {/* ── AI Assist command bar (Phase 6) ── */}
      {assistModeActive && onRequestTree && (
        <AssistBar
          activeTabId={activeTabId}
          onRequestTree={onRequestTree}
          onRequestCapture={onRequestCapture}
          onAddSteps={onAddSteps}
        />
      )}

      {/* Tier-2 Draft Steps panel */}
      {draftStepsResult && draftStepsResult.status !== 'idle' && (
        <div style={{
          padding: '8px 14px',
          background: 'rgba(124,58,237,0.06)',
          borderBottom: '1px solid rgba(124,58,237,0.2)',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 600, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 6 }}>
            🤖 Step Suggestions
            {draftStepsResult.status === 'loading' && <span style={{ color: '#7c3aed', fontWeight: 400 }}>generating…</span>}
          </div>
          <pre style={{
            fontSize: 11, color: '#c4b5fd', margin: 0,
            whiteSpace: 'pre-wrap', lineHeight: 1.6,
            maxHeight: 180, overflowY: 'auto',
          }}>
            {draftStepsResult.text || (draftStepsResult.status === 'loading' ? '▊' : '')}
            {draftStepsResult.status === 'loading' && draftStepsResult.text && '▊'}
          </pre>
        </div>
      )}

      {/* ── Custom Commands ──
           When cmdBuilderActive: takes flex:1 (full remaining height), step list hidden.
           When NOT active: compact strip with tabs + grid only.                         */}
      <div style={{
        ...(cmdBuilderActive
          ? { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
          : { borderBottom: '1px solid #27272a', flexShrink: 0 }),
      }}>
        <CustomCommandsPanel
          onAddSteps={onAddSteps}
          onRequestCapture={onRequestCapture}
          captureActive={captureActive}
          onBuilderActiveChange={setCmdBuilderActive}
        />
      </div>

      {/* ── Scrollable area: step list + replay + test script ──
           Hidden while CommandBuilder is open so the builder gets full panel height. */}
      {!cmdBuilderActive && (
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

      {/* ── Inspect mode panel ── */}
      {mode === 'inspect' && !assistModeActive && (
        <InspectPanel
          node={inspectedNode ?? null}
          onAddAssertion={(assertion, selector) => {
            const step = {
              id: newId(),
              type: 'assert' as const,
              label: `Assert ${assertion}: ${selector.slice(0, 40)}`,
              selector: {
                description: selector,
                candidates: [{ strategy: 'text' as const, value: selector, addedBy: 'user' as const }],
              },
              assertion: assertion as 'visible' | 'notVisible' | 'enabled' | 'disabled' | 'hasText',
            };
            onAddSteps([step as unknown as IRStep]);
          }}
        />
      )}

      {/* Instruction text */}
      {mode !== 'inspect' && !assistModeActive && steps.length === 0 && (
        <div style={{
          margin: '10px 14px', padding: '8px 12px',
          background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)',
          borderRadius: 6, fontSize: 12, color: '#93c5fd', flexShrink: 0,
        }}>
          Click on the browser mirror to record tap steps. Use the action buttons above for navigation and keyboard input.
        </div>
      )}
      {assistModeActive && steps.length === 0 && (
        <div style={{
          margin: '10px 14px', padding: '8px 12px',
          background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)',
          borderRadius: 6, fontSize: 12, color: '#c4b5fd', flexShrink: 0,
        }}>
          Type a step in plain English above — e.g. "click the button called Sign in".
          Each sentence resolves to a real element on the page before it's added.
        </div>
      )}

      {/* Step list — hidden in Inspect mode so InspectPanel gets full space */}
      <div style={{ flexShrink: 0, display: mode === 'inspect' && !assistModeActive ? 'none' : 'block' }}>
        <StepList
          steps={steps}
          replayingIndex={replayingIndex}
          stepStatuses={stepStatuses}
          onDelete={onDeleteStep}
          onEdit={onEditStep}
          onEditCandidates={onEditCandidates}
          onReorder={onReorderStep}
          onReplay={onReplay}
          compact
        />
      </div>

      {/* ── Replay section ── */}
      {steps.length > 0 && (
        <div style={{ borderTop: '1px solid #27272a', padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#a1a1aa' }}>
              Replay&nbsp;&nbsp;
              {replayRan && (
                <span style={{ color: passCount === steps.length ? '#22c55e' : '#ef4444', fontWeight: 400 }}>
                  {passCount}/{steps.length} ok
                </span>
              )}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {replayRan && (
                <button onClick={openReport} style={{ background: '#1c1c1f', border: '1px solid #3f3f46', borderRadius: 6, padding: '5px 10px', color: '#a1a1aa', cursor: 'pointer', fontSize: 11 }}>
                  📊 Report
                </button>
              )}
              <button
                onClick={onReplay}
                disabled={steps.length === 0 || activeTabId < 0}
                style={{ background: '#7c3aed', border: 'none', borderRadius: 6, padding: '5px 14px', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, opacity: activeTabId < 0 ? 0.5 : 1 }}
              >
                ▶ Replay {steps.length} step{steps.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#71717a' }}>Delay between steps:</span>
            <span style={{
              background: '#18181b', border: '1px solid #3f3f46', borderRadius: 4,
              padding: '2px 8px', fontSize: 12, color: '#e4e4e7',
            }}>800</span>
            <span style={{ fontSize: 12, color: '#71717a' }}>ms</span>
          </div>
          {/* Replay results */}
          {replayRan && Object.entries(stepStatuses).map(([stepId, status], i) => {
            const step = steps.find(s => s.id === stepId);
            if (!step) return null;
            const label = (step as { label?: string }).label ?? step.type;
            return (
              <div key={stepId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12 }}>
                <span style={{ minWidth: 18, color: '#52525b' }}>{i + 1}</span>
                <span style={{
                  padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                  background: status === 'ok' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  color: status === 'ok' ? '#22c55e' : '#ef4444',
                }}>
                  {status === 'ok' ? 'OK' : status === 'not_found' ? 'MISS' : 'ERR'}
                </span>
                <span style={{ color: '#a1a1aa', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {step.type} {label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Network Events (collapsible) ── */}
      {networkEvents.length > 0 && (
        <NetworkEventsPanel
          events={networkEvents}
          onAddAssert={onAddNetworkAssert}
          onClear={onClearNetworkEvents}
        />
      )}

      {/* ── Test Script (collapsible, JSON + Playwright) ── */}
      {steps.length > 0 && (
        <div style={{ borderTop: '1px solid #27272a' }}>
          <button
            onClick={() => setScriptOpen(v => !v)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', background: 'none', border: 'none',
              color: '#a1a1aa', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            <span>{scriptOpen ? '▼' : '▶'} Test Script</span>
            {scriptOpen && (
              <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => navigator.clipboard.writeText(scriptMode === 'json' ? scriptJson : scriptPw)}
                  style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 4, padding: '2px 8px', color: '#71717a', cursor: 'pointer', fontSize: 11 }}
                >Copy</button>
                <button
                  onClick={() => {
                    const content = scriptMode === 'json' ? scriptJson : scriptPw;
                    const ext = scriptMode === 'json' ? '.json' : '.spec.ts';
                    const name = (loadedTestName || 'test').replace(/\s+/g, '-').toLowerCase() + ext;
                    const blob = new Blob([content], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = name; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 4, padding: '2px 8px', color: '#71717a', cursor: 'pointer', fontSize: 11 }}
                >↓ Save</button>
              </div>
            )}
          </button>
          {scriptOpen && (
            <>
              {/* Format switcher */}
              <div style={{ display: 'flex', margin: '0 14px 8px', borderRadius: 6, overflow: 'hidden', border: '1px solid #3f3f46' }}>
                {(['json', 'playwright'] as const).map(m => (
                  <button
                    key={m}
                    onClick={() => setScriptMode(m)}
                    style={{
                      flex: 1, padding: '4px 8px', fontSize: 11,
                      background: scriptMode === m ? '#3f3f46' : 'none',
                      border: 'none', color: scriptMode === m ? '#e4e4e7' : '#71717a',
                      cursor: 'pointer', fontWeight: scriptMode === m ? 600 : 400,
                    }}
                  >
                    {m === 'json' ? 'IR JSON' : 'Playwright .spec.ts'}
                  </button>
                ))}
              </div>
              <pre style={{
                margin: '0 14px 14px',
                background: '#09090b', border: '1px solid #27272a', borderRadius: 6,
                padding: 10, fontSize: 11, color: '#a1a1aa',
                overflowX: 'auto', maxHeight: 300, overflowY: 'auto',
                fontFamily: 'monospace', lineHeight: 1.5,
              }}>
                {scriptMode === 'json' ? scriptJson : scriptPw}
              </pre>
            </>
          )}
        </div>
      )}

      </div>
      )}
    </div>
  );
}

// ── NetworkEventsPanel ────────────────────────────────────────────────────────

function NetworkEventsPanel({ events, onAddAssert, onClear }: {
  events: NetworkEvent[];
  onAddAssert: (e: NetworkEvent) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);

  const statusColor = (s: number) =>
    s >= 500 ? '#ef4444' : s >= 400 ? '#f59e0b' : s >= 200 ? '#22c55e' : '#71717a';

  const shortUrl = (url: string) => {
    try {
      const u = new URL(url);
      return u.pathname + (u.search ? '?' + u.searchParams.toString().slice(0, 30) : '');
    } catch { return url.slice(0, 50); }
  };

  return (
    <div style={{ borderTop: '1px solid #27272a' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', background: 'none', border: 'none',
          color: '#a1a1aa', cursor: 'pointer', fontSize: 12, fontWeight: 600,
        }}
      >
        <span>{open ? '▼' : '▶'} Network {events.length}</span>
        {open && (
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 4, padding: '2px 8px', color: '#71717a', cursor: 'pointer', fontSize: 11 }}
          >Clear</button>
        )}
      </button>
      {open && (
        <div style={{ margin: '0 14px 14px' }}>
          {events.slice(-20).map(ev => (
            <div
              key={ev.requestId}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 8px', borderRadius: 4, marginBottom: 3,
                background: '#111113', border: '1px solid #1c1c1f',
              }}
            >
              <span style={{ fontSize: 9, fontWeight: 700, color: '#a1a1aa', flexShrink: 0, background: '#27272a', borderRadius: 3, padding: '1px 4px' }}>
                {ev.method}
              </span>
              <span style={{ fontSize: 10, color: '#71717a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ev.url}>
                {shortUrl(ev.url)}
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, color: statusColor(ev.status), flexShrink: 0 }}>
                {ev.status}
              </span>
              <span style={{ fontSize: 10, color: '#52525b', flexShrink: 0 }}>{ev.durationMs}ms</span>
              <button
                onClick={() => onAddAssert(ev)}
                title="Add networkAssert step"
                style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 3, padding: '1px 5px', color: '#7c3aed', cursor: 'pointer', fontSize: 9, flexShrink: 0 }}
              >+ Assert</button>
            </div>
          ))}
          <div style={{ fontSize: 11, color: '#3f3f46', marginTop: 6, textAlign: 'center' }}>
            Click "+ Assert" to add a networkAssert step for an API call
          </div>
        </div>
      )}
    </div>
  );
}

// ── InspectPanel helpers ───────────────────────────────────────────────────────

const STRATEGY_COLOR: Record<string, string> = {
  a11yId: '#22c55e', text: '#22c55e', contentDesc: '#22c55e', className: '#22c55e',
  css: '#ef4444', xpath: '#ef4444', textContains: '#eab308', textRegex: '#eab308',
};

// Stability classification — ported from PLEX_Locator_Algorithm.md §10
// Maps each locator strategy + value to STABLE / INDEXED / FRAGILE
// with a human-readable note matching Mimiq's "Unique text content." style.
type Stability = { label: 'STABLE' | 'INDEXED' | 'FRAGILE'; color: string; bg: string; note: string };

function getXPathStability(xpath: string): Stability {
  if (!xpath || !xpath.startsWith('//')) {
    return { label: 'FRAGILE', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', note: 'No valid XPath available.' };
  }
  // Level 1 — unique semantic attribute (most stable)
  if (/@data-testid=/.test(xpath) || /\[@id=/.test(xpath)) {
    return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Anchored to unique ID attribute.' };
  }
  if (/@aria-label=/.test(xpath)) {
    return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Anchored to aria-label attribute.' };
  }
  if (/normalize-space/.test(xpath)) {
    return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Unique text content.' };
  }
  // Level 2 — indexed occurrence [N]
  if (/\[\d+\]/.test(xpath)) {
    return { label: 'INDEXED', color: '#eab308', bg: 'rgba(234,179,8,0.08)', note: 'Position-indexed — may break if list order changes.' };
  }
  // Level 3 — ancestor anchor + @index path
  if (/@index=/.test(xpath)) {
    return { label: 'INDEXED', color: '#eab308', bg: 'rgba(234,179,8,0.08)', note: 'Anchored to nearest ancestor identifier.' };
  }
  // Level 4 — purely structural
  return { label: 'FRAGILE', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', note: 'Structural path — breaks on any DOM restructure.' };
}

function getCSSStability(css: string): Stability {
  if (!css) return { label: 'FRAGILE', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', note: 'No CSS selector available.' };
  if (/^\[data-testid=|^\[data-cy=|^\[data-qa=/.test(css)) {
    return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Unique test-id attribute.' };
  }
  if (/^#[^:\s]+$/.test(css)) {
    return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Unique element ID.' };
  }
  if (/nth-of-type|nth-child/.test(css)) {
    return { label: 'FRAGILE', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', note: 'Positional selector — breaks on DOM changes.' };
  }
  return { label: 'INDEXED', color: '#eab308', bg: 'rgba(234,179,8,0.08)', note: 'May match multiple elements.' };
}

function getLocatorStability(strategy: string, value: string): Stability {
  switch (strategy) {
    case 'a11yId':
      return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Unique test ID — survives most UI changes.' };
    case 'text':
      return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'Exact visible text.' };
    case 'contentDesc':
      return { label: 'STABLE', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', note: 'ARIA label attribute.' };
    case 'textContains':
      return { label: 'INDEXED', color: '#eab308', bg: 'rgba(234,179,8,0.08)', note: 'Partial text — may match multiple elements.' };
    case 'className':
      return { label: 'INDEXED', color: '#eab308', bg: 'rgba(234,179,8,0.08)', note: 'Role-based — depends on context.' };
    case 'css':
      return getCSSStability(value);
    case 'xpath':
      return getXPathStability(value);
    default:
      return { label: 'INDEXED', color: '#eab308', bg: 'rgba(234,179,8,0.08)', note: '' };
  }
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      style={{ background: 'none', border: '1px solid #3f3f46', borderRadius: 3,
        padding: '1px 6px', color: copied ? '#22c55e' : '#52525b',
        cursor: 'pointer', fontSize: 9, flexShrink: 0 }}
      title="Copy to clipboard"
    >{copied ? '✓' : '📋'}</button>
  );
}

function InspectSectionHeader({ label }: { label: string }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#52525b',
      letterSpacing: '0.1em', padding: '10px 14px 4px',
      borderTop: '1px solid #1c1c1f', marginTop: 2 }}>
      {label}
    </div>
  );
}

function LocatorRow({ strategy, value, isPrimary }: { strategy: string; value: string; isPrimary?: boolean }) {
  const color   = STRATEGY_COLOR[strategy] ?? '#71717a';
  const stab    = getLocatorStability(strategy, value);
  return (
    <div style={{ borderBottom: '1px solid #111113', background: isPrimary ? 'rgba(34,197,94,0.04)' : 'transparent' }}>
      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px' }}>
        <span style={{ fontSize: 9, fontWeight: 700, color, border: `1px solid ${color}`,
          borderRadius: 3, padding: '1px 6px', fontFamily: 'monospace', flexShrink: 0, minWidth: 70, textAlign: 'center' }}>
          {strategy}
        </span>
        <span style={{ flex: 1, fontSize: 11, color: isPrimary ? '#e4e4e7' : '#a1a1aa',
          fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={value}>
          {value}
        </span>
        {/* Stability badge */}
        <span style={{
          fontSize: 9, fontWeight: 700, color: stab.color,
          background: stab.bg, border: `1px solid ${stab.color}44`,
          borderRadius: 3, padding: '1px 6px', flexShrink: 0,
        }} title={stab.note}>
          {stab.label}
        </span>
        {isPrimary && <span style={{ fontSize: 9, color: '#22c55e', flexShrink: 0, fontWeight: 700 }}>PRIMARY</span>}
        <CopyButton value={value} />
      </div>
      {/* Quality note (like Mimiq's "Unique text content.") */}
      {stab.note && (
        <div style={{ padding: '0 14px 5px 96px', fontSize: 10, color: '#52525b', fontStyle: 'italic' }}>
          {stab.note}
        </div>
      )}
    </div>
  );
}

function LocatorFieldRow({ label, value }: { label: string; value?: string }) {
  const present = value && value.trim().length > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '5px 14px',
      borderBottom: '1px solid #111113' }}>
      <span style={{ fontSize: 11, color: '#52525b', minWidth: 90, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 11, color: present ? '#a1a1aa' : '#3f3f46',
        fontStyle: present ? 'normal' : 'italic', flex: 1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
        {present ? value : 'no value'}
      </span>
      {present && <CopyButton value={value!} />}
    </div>
  );
}

// ARIA role → HTML tag mapping (must mirror background.ts ARIA_TO_HTML_TAG).
// XPath uses HTML element tags, NEVER ARIA role names as tag selectors.
// e.g. role "link" → tag "a";  using "//link[...]" finds <link rel="stylesheet">, NOT anchors.
const ARIA_ROLE_TO_HTML_TAG: Record<string, string> = {
  link: 'a', button: 'button', textbox: 'input', searchbox: 'input',
  combobox: 'select', listbox: 'select', checkbox: 'input', radio: 'input',
  img: 'img', image: 'img', list: 'ul', listitem: 'li',
  table: 'table', row: 'tr', cell: 'td', columnheader: 'th',
  form: 'form', main: 'main', navigation: 'nav', banner: 'header',
  contentinfo: 'footer', heading: 'h2', paragraph: 'p',
};

function resolveXPathTag(node: SerializedAXNode): string {
  if (node.tagName) return node.tagName;                  // DOM-computed — always correct
  return (node.role ? ARIA_ROLE_TO_HTML_TAG[node.role] : undefined) ?? '*';
}

/** Generate a best-effort XPath from ARIA node data — Studio-side fallback.
 *  Used when the extension's enrichWithDOMInfo couldn't run (e.g. extension not reloaded yet). */
function generateXPathFromNode(node: SerializedAXNode): { xpath: string; generated: boolean } | null {
  const attrs = node.allAttributes ?? node.attributes ?? {};
  const testId = attrs['data-testid'] ?? attrs['data-cy'] ?? attrs['data-qa'] ?? attrs['data-test'];
  if (testId)    return { xpath: `//*[@data-testid="${testId}"]`, generated: true };
  const ariaLabel = node.description ?? attrs['aria-label'];
  if (ariaLabel) return { xpath: `//*[@aria-label="${ariaLabel.replace(/"/g, "'")}"]`, generated: true };
  const idAttr = attrs['id'];
  if (idAttr && !/^\d/.test(idAttr)) return { xpath: `//*[@id="${idAttr}"]`, generated: true };
  if (node.name && node.name.length < 80) {
    // Use correct HTML tag — "a" for link role, NOT "link" which matches <link rel="stylesheet">
    const tag = resolveXPathTag(node);
    return { xpath: `//${tag}[normalize-space(.)="${node.name.replace(/"/g, "'")}"]`, generated: true };
  }
  if (node.role) return { xpath: `//*[@role="${node.role}"]`, generated: true };
  return null;
}

// XPathSection — always shown in Inspect panel with stability badge + Mimiq-style note
function XPathSection({ xpath, isGenerated }: { xpath: string; isGenerated?: boolean }) {
  const stab = getXPathStability(xpath);
  const [copied, setCopied] = useState(false);

  return (
    <div style={{
      margin: '0 14px 8px',
      borderRadius: 7,
      border: `1.5px solid ${stab.color}44`,
      background: stab.bg,
      overflow: 'hidden',
    }}>
      {/* Stability header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 10px',
        borderBottom: `1px solid ${stab.color}22`,
        background: `${stab.color}11`,
      }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: stab.color, letterSpacing: '0.06em' }}>
          {stab.label === 'STABLE' ? '✓ STABLE' : stab.label === 'INDEXED' ? '⚠ INDEXED' : '✗ FRAGILE'}
        </span>
        <span style={{ fontSize: 10, color: '#71717a', flex: 1 }}>
          {stab.note}
        </span>
        {/* "Generated" badge when DOM enrichment wasn't possible */}
        {isGenerated && (
          <span style={{ fontSize: 9, color: '#eab308', border: '1px solid #854d0e44',
            borderRadius: 3, padding: '1px 5px', flexShrink: 0 }}
            title="Generated from ARIA attributes — reload extension for DOM-computed XPath">
            ARIA-derived
          </span>
        )}
        <button
          onClick={() => { void navigator.clipboard.writeText(xpath); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
          style={{ background: 'none', border: `1px solid ${stab.color}44`, borderRadius: 3,
            padding: '1px 7px', color: copied ? stab.color : '#52525b',
            cursor: 'pointer', fontSize: 9, flexShrink: 0 }}
        >{copied ? '✓ Copied' : '📋 Copy'}</button>
      </div>
      {/* XPath value */}
      <div style={{ padding: '8px 10px' }}>
        <code style={{
          fontSize: 11, color: '#e4e4e7', fontFamily: 'monospace',
          wordBreak: 'break-all', lineHeight: 1.6, display: 'block',
        }}>
          {xpath}
        </code>
      </div>
      {/* Stability explanation + ARIA-derived note */}
      <div style={{ padding: '5px 10px 7px', fontSize: 10, color: '#52525b', borderTop: `1px solid ${stab.color}11` }}>
        {stab.label === 'STABLE'
          ? '✓ Safe to use in tests — anchored to a stable attribute that survives UI changes.'
          : stab.label === 'INDEXED'
          ? '⚠ Use with caution — works now but may fail if list order or sibling count changes.'
          : '✗ Avoid for tests — structural path will break on any DOM restructure. Add a data-testid instead.'}
        {isGenerated && (
          <span style={{ color: '#854d0e', marginLeft: 6 }}>
            · Reload the extension for a DOM-computed path.
          </span>
        )}
      </div>
    </div>
  );
}

function InspectPanel({ node, onAddAssertion }: {
  node: SerializedAXNode | null;
  onAddAssertion?: (assertion: string, selector: string) => void;
}) {
  const [assertionType, setAssertionType] = useState('visible');

  if (!node) {
    return (
      <div style={{ margin: '10px 14px', padding: '20px 14px',
        background: 'rgba(96,165,250,0.04)', border: '1px solid rgba(96,165,250,0.15)',
        borderRadius: 8, textAlign: 'center' }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#93c5fd' }}>Inspect Mode</div>
        <div style={{ fontSize: 11, color: '#71717a', marginTop: 6, lineHeight: 1.5 }}>
          Click any element on the browser mirror<br />to inspect its ARIA attributes and locators.
        </div>
        <div style={{ fontSize: 11, color: '#52525b', marginTop: 10 }}>
          Switch back to <strong style={{ color: '#a1a1aa' }}>Drive</strong> to resume recording.
        </div>
      </div>
    );
  }

  const attrs = node.allAttributes ?? node.attributes ?? {};

  // Build ordered locator candidates (ARIA-first: best → worst stability)
  // XPath is intentionally excluded here — it gets its own mandatory dedicated section below.
  const plex: Array<{ strategy: string; value: string }> = [];
  const testId = attrs['data-testid'] ?? attrs['data-cy'] ?? attrs['data-qa'] ?? attrs['data-test'];
  if (testId)             plex.push({ strategy: 'a11yId',       value: testId });
  if (node.name)          plex.push({ strategy: 'text',          value: node.name });
  if (node.description && node.description !== node.name)
                          plex.push({ strategy: 'contentDesc',   value: node.description });
  if (node.placeholder)   plex.push({ strategy: 'textContains',  value: node.placeholder });
  if (node.cssSelector)   plex.push({ strategy: 'css',           value: node.cssSelector });

  // Element locators table (text / aria-label / id / placeholder rows)
  const elemLocators: Array<{ label: string; value?: string }> = [
    { label: 'text',         value: node.name },
    { label: 'aria-label',   value: node.description },
    { label: 'id / testid',  value: testId ?? attrs['id'] },
    { label: 'placeholder',  value: node.placeholder ?? attrs['placeholder'] },
    { label: 'value',        value: node.value ?? attrs['value'] },
  ].filter(r => r.label === 'text' || r.label === 'aria-label' || r.value);

  // Interesting HTML attributes (excluding ones already shown)
  const shownKeys = new Set(['data-testid','data-cy','data-qa','data-test','id','placeholder','value','aria-label']);
  const extraAttrs = Object.entries(attrs).filter(([k]) => !shownKeys.has(k) && k !== 'aria-labelledby');

  const size = node.bounds ? `${node.bounds.width} × ${node.bounds.height} px` : undefined;
  const bounds = node.bounds ? `[${node.bounds.x}, ${node.bounds.y}][${node.bounds.x + node.bounds.width}, ${node.bounds.y + node.bounds.height}]` : undefined;

  const primarySelector = plex[0]?.value ?? '';

  return (
    <div style={{ flexShrink: 0 }}>

      {/* ── Role + state ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        background: '#18181b', borderBottom: '1px solid #1c1c1f' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa',
          background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)',
          borderRadius: 5, padding: '2px 8px' }}>
          {node.tagName ?? node.role}
        </span>
        {node.role && node.tagName && node.role !== node.tagName && (
          <span style={{ fontSize: 10, color: '#52525b' }}>{node.role}</span>
        )}
        {!node.disabled && (
          <span style={{ fontSize: 10, color: '#22c55e', border: '1px solid #166534',
            borderRadius: 4, padding: '1px 6px' }}>enabled</span>
        )}
        {node.disabled && (
          <span style={{ fontSize: 10, color: '#71717a', border: '1px solid #27272a',
            borderRadius: 4, padding: '1px 6px' }}>disabled</span>
        )}
        {node.checked !== undefined && (
          <span style={{ fontSize: 10, color: node.checked ? '#22c55e' : '#71717a' }}>
            {node.checked ? '☑ checked' : '☐ unchecked'}
          </span>
        )}
      </div>

      {/* ── Element Locators ── */}
      <InspectSectionHeader label="ELEMENT LOCATORS" />
      {elemLocators.map(r => <LocatorFieldRow key={r.label} label={r.label} value={r.value} />)}

      {/* ── Plex Selectors (ARIA-first) ── */}
      <InspectSectionHeader label="SCRIPTWAVE SELECTORS" />
      {plex.length === 0 ? (
        <div style={{ padding: '6px 14px', fontSize: 11, color: '#3f3f46' }}>No ARIA selectors found</div>
      ) : (
        plex.map((c, i) => <LocatorRow key={c.strategy + i} strategy={c.strategy} value={c.value} isPrimary={i === 0} />)
      )}

      {/* ── XPath — always shown, mandatory section ── */}
      {/* Always visible regardless of ARIA availability.
          Priority: (1) DOM-computed xpathSelector from enrichWithDOMInfo
                    (2) Studio-side ARIA-derived fallback from generateXPathFromNode
          The "ARIA-derived" badge appears when using (2). */}
      <InspectSectionHeader label="XPATH" />
      {(() => {
        if (node.xpathSelector) {
          return <XPathSection xpath={node.xpathSelector} isGenerated={false} />;
        }
        const fallback = generateXPathFromNode(node);
        if (fallback) {
          return <XPathSection xpath={fallback.xpath} isGenerated={true} />;
        }
        return (
          <div style={{ padding: '8px 14px 10px', fontSize: 11, color: '#52525b' }}>
            <span style={{ color: '#3f3f46' }}>⚠</span> No XPath could be computed — element has no unique identifier.
            <div style={{ marginTop: 4, color: '#3f3f46', fontSize: 10 }}>
              Add a <code style={{ fontFamily: 'monospace', color: '#7c3aed' }}>data-testid</code> attribute
              to this element for stable test automation.
            </div>
          </div>
        );
      })()}

      {/* ── HTML Attributes ── */}
      {extraAttrs.length > 0 && (
        <>
          <InspectSectionHeader label="HTML ATTRIBUTES" />
          {extraAttrs.slice(0, 12).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 10, padding: '4px 14px',
              borderBottom: '1px solid #111113', alignItems: 'baseline' }}>
              <span style={{ fontSize: 10, color: '#7c3aed', fontFamily: 'monospace', minWidth: 90, flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: 10, color: '#a1a1aa', fontFamily: 'monospace',
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</span>
            </div>
          ))}
        </>
      )}

      {/* ── Element Info ── */}
      <InspectSectionHeader label="ELEMENT INFO" />
      {[
        { label: 'tag',    value: node.tagName },
        { label: 'bounds', value: bounds },
        { label: 'size',   value: size },
        { label: 'role',   value: node.role },
      ].filter(r => r.value).map(r => (
        <div key={r.label} style={{ display: 'flex', gap: 10, padding: '4px 14px',
          borderBottom: '1px solid #111113' }}>
          <span style={{ fontSize: 11, color: '#52525b', minWidth: 55, flexShrink: 0 }}>{r.label}</span>
          <span style={{ fontSize: 11, color: '#a1a1aa', fontFamily: 'monospace' }}>{r.value}</span>
        </div>
      ))}

      {/* ── Ancestors ── */}
      {node.ancestorPath && node.ancestorPath.length > 0 && (
        <>
          <InspectSectionHeader label="ANCESTORS" />
          <div style={{ padding: '6px 14px', fontSize: 11, color: '#60a5fa', fontFamily: 'monospace',
            borderBottom: '1px solid #1c1c1f' }}>
            {node.ancestorPath.join(' › ')}
          </div>
        </>
      )}

      {/* ── Add Assertion to Steps ── */}
      {plex.length > 0 && (
        <>
          <InspectSectionHeader label="ADD ASSERTION TO STEPS" />
          <div style={{ padding: '8px 14px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={assertionType} onChange={e => setAssertionType(e.target.value)}
              style={{ flex: 1, background: '#09090b', border: '1px solid #3f3f46',
                borderRadius: 5, padding: '6px 8px', color: '#e4e4e7', fontSize: 11, outline: 'none' }}>
              <option value="visible">Visible</option>
              <option value="notVisible">Not Visible</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
              {node.name && <option value="hasText">Has Text</option>}
            </select>
            <button
              onClick={() => onAddAssertion?.(assertionType, primarySelector)}
              style={{ background: '#7c3aed', border: 'none', borderRadius: 5,
                padding: '6px 14px', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
              + Add
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const actionBtn: React.CSSProperties = {
  background: 'none',
  border: '1px solid #3f3f46',
  borderRadius: 5,
  padding: '5px 10px',
  color: '#a1a1aa',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
